import { Component, ViewChild } from '@angular/core';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Logger } from 'common/utility/logger/logger';
import { EdgeDeviceSelectComponent } from 'components/edge-device-select/edge-device-select.component';
import { edgeSample, getEdgeSample } from 'components/edge-device/edgeData';
import { Subject, take, timer } from 'rxjs';
import { EdgeDeviceDataService } from 'services/edge-device/edge-device-data.service';
import { Alert, AlertLevel } from 'services/edge-device/edge-device-interfaces';
import { EdgeDevice } from 'services/edge-device/edge-device.service';


const DeviceTabIndex = 1
const TabAnimationDuration = 200

@Component({
  selector: 'ems-edge-device-dashboard',
  templateUrl: './edge-device-dashboard.component.html',
  styleUrls: ['./edge-device-dashboard.component.scss']
})
export class EdgeDeviceDashboardComponent {

  @ViewChild(MatTabGroup) private tabGroup!: MatTabGroup;
  @ViewChild('deviceSubTabs') private deviceSubTabs!: MatTabGroup;
  @ViewChild(EdgeDeviceSelectComponent) private deviceSelect!: EdgeDeviceSelectComponent;

  private unsubscribe: Subject<void> = new Subject();
  private edgeDevice: EdgeDevice | null = null
  private deviceData: any
  private partitions: any
  private interfaces: any
  private alertClicked: boolean = false

  public get MemoryInfo(): any {
    return this.edgeDeviceDataService.DeviceMemoryInfo(this.edgeDevice?.edgeId)
  }

  public get SelectedDevice(): EdgeDevice {
    return this.edgeDevice ?? {
      edgeId: "",
      label: ""
    }
  }

  public get DeviceData(): any {
    return this.deviceData
  }

  public get SystemInfo(): any {
    return this.DeviceData?.system?.system_info
  }

  public get TabAnimationDuration(): string{
    return `${TabAnimationDuration}ms`
  }

  public get CPUInfo(): any {
    return this.DeviceData?.system?.cpu
  }

  public get Memory(): any {
    return this.DeviceData?.system?.memory
  }

  public get Disk(): any {
    return this.DeviceData?.system?.disk
  }

  public get Network(): any {
    return this.DeviceData?.system?.network
  }

  public get Partitions(): any[] {
    return this.partitions
  }

  public get Interfaces(): any[] {
    return this.interfaces
  }

  public get Logs(): any {
    return this.DeviceData?.logs
  }

  public get Containers(): any[] {
    return this.DeviceData?.containers?.containers
  }

  public get Streams(): any[] {
    return this.DeviceData?.stream_manager?.streams
  }

  public get CanInterface(): any {
    return this.DeviceData?.can_interface
  }

  private alerts: Alert[] | undefined

  public get Alerts(): Alert[] {
    return this.alerts ??= this.combineAlerts()
  }

  constructor(
    private edgeDeviceDataService: EdgeDeviceDataService
  ) { }


  get deviceAlerts(): Alert[] {
    return this.Alerts.filter(a => a.device_id === this.SelectedDevice.edgeId);
  }

  public get highestAlert(): string {
    const alerts = this.alertCounts
    return alerts.critical ? "error" : alerts.warning ? "warning" : "info"
  }

  get AllAlertsCount(): number {
    return this.Alerts.length
  }

  get alertCounts() {
    return {
      critical: this.Alerts.filter(a => a.level === 'critical').length,
      warning: this.Alerts.filter(a => a.level === 'warning').length,
      info: this.Alerts.filter(a => a.level === 'info').length,
    };
  }

  get AllDeviceAlertsCount(): number {
    return this.deviceAlerts.length
  }

  get deviceAlertCounts() {
    const alerts = this.deviceAlerts
    return {
      critical: alerts.filter(a => a.level === 'critical').length,
      warning: alerts.filter(a => a.level === 'warning').length,
      info: alerts.filter(a => a.level === 'info').length,
    };
  }

  public get highestDeviceAlert(): string {
    const alerts = this.deviceAlertCounts
    return alerts.critical ? "error" : alerts.warning ? "warning" : "info"
  }

  public get ShowDeviceTab(): boolean {
    // this.alertClicked && (this.showDeviceAlertsTab(), this.alertClicked = false)
    return !!this.SelectedDevice.edgeId
  }

  alertIcon(level: AlertLevel): string {
    return { critical: 'error', warning: 'warning', info: 'info' }[level];
  }

  get dateString(): string {
    return new Date().toISOString().slice(0, 10);
  }

  public onAlertDeviceClick(deviceId: string): void {
    this.alertClicked = true
    this.deviceSelect.initialEdgeId = deviceId;
    // this.showDeviceAlertsTab()
  }

  public onTabChange(event: MatTabChangeEvent) {
    if (event.index == DeviceTabIndex && this.alertClicked == true) {
      this.alertClicked = false
      this.showDeviceAlertsTab(TabAnimationDuration + 80)
    }
  }

  private showDeviceAlertsTab(delay: number): void {
    timer(delay).pipe(take(1)).subscribe(_ => {
      this.tabGroup.selectedIndex = 1;
      this.deviceSubTabs && (this.deviceSubTabs.selectedIndex = 5);
    })
  }

  public LoadEdgeDevice(edgeDevice: EdgeDevice): void {
    this.edgeDevice = edgeDevice
    this.deviceData = getEdgeSample(edgeDevice.edgeId)
    this.createPartitions()
    this.createInterfaces()

    if (edgeDevice?.label) {
      this.tabGroup.selectedIndex = 1;
    }
  }

  ngOnInit(): void {
    Logger.debug("Initializing Edge Device Dashboard", this.MemoryInfo)
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  get containerSummary() {
    const containers = this.Containers
    let returnValue

    if (containers) {
      returnValue = {
        total: containers.length,
        running: containers.filter(c => c.state.running).length,
        stopped: containers.filter(c => !c.state.running || !!c.state.exit_code).length,
        paused: containers.filter(c => c.state.paused).length,
        restarting: containers.filter(c => c.state.restarting).length,
      };
    }
    else {
      returnValue = {
        total: 0,
        running: 0,
        stopped: 0,
        paused: 0,
        restarting: 0,
      };
    }

    return returnValue
  }

  shortId(id: string): string { return id.slice(0, 12); }

  get logSummary(): any {
    const errorCount = this.Logs?.errors?.length || 0
    const warningCount = this.Logs?.warnings?.length || 0

    return {
      total: errorCount + warningCount,
      warnings: warningCount,
      errors: errorCount
    };
  }


  get streamSummary() {
    const streams = this.Streams
    if (!this.Streams) {
      return {
        total: 0,
        active: 0,
        idle: 0,
        messages: 0,
      };

    }
    return {
      total: streams.length,
      active: streams.filter(stream => !!stream.message_count).length,
      idle: streams.filter(stream => !stream.message_count).length,
      messages: streams.reduce((sum, s) => sum + (s.message_count ?? 0), 0),
    };
  }

  private combineAlerts(): Array<any> {
    const devices = Object.keys(edgeSample)

    const alerts = devices.flatMap(id => {
      const sample = getEdgeSample(id)
      if (!sample) return []
      return (sample.alerts ?? []).map((alert: any) => ({ ...alert, device_id: sample.device_id }))
    })

    return alerts
  }


  private createPartitions(): void {
    const partitions = this.Disk?.partitions

    if (!partitions) {
      this.partitions = []
      return
    }

    this.partitions = Object.entries(partitions).map(([mount, data]: [string, any]) => ({ mount, ...data }))
  }

  private createInterfaces(): void {
    const interfaces = this.Network?.interfaces

    if (!interfaces) {
      this.interfaces = []
      return
    }

    this.interfaces = Object.entries(interfaces).map(([name, data]: [string, any]) => ({ name, ...data }))
  }


}
